import rand from "@/utils/rand";
import {AwsService} from "@/simulation/AwsService";
import {Lambda} from "@/simulation/Lambda";
import wait from "@/utils/wait";

class Message {
    id = rand();
    visible = true;
    processing = false;
    retries = 0;
    visibilityPromise = Promise.resolve();
}

export class Sqs implements AwsService {
    id = rand();
    name = 'SQS';
    messages: Array<Message> = [];
    receivingLambda?: Lambda;
    dlq?: Sqs;
    successes = 0;
    errors = 0;
    retryCount?: number;
    retryDelay = 3000;
    batchSize = 1;
    private interval?: number;

    constructor() {
        this.interval = window.setInterval(() => this.invokeLambda(), 300);
    }

    invoke(message?: Message) {
        if (!message) {
            message = new Message;
        }
        this.messages.push(message);
    }

    retry(message: Message) {
        message.visible = false;
        message.processing = false;
        message.retries++;
        message.visibilityPromise = wait(this.retryDelay).then(() => {
            message.visible = true;
        });
    }

    failedMessage(message: Message) {
        this.errors++;
        this.deleteMessage(message);
        if (this.dlq) {
            this.dlq.invoke(message);
        }
    }

    deleteMessage(message: Message) {
        const index = this.messages.indexOf(message);
        if (index > -1) {
            this.messages.splice(index, 1);
        }
    }

    connectToLambda(lambda: Lambda) {
        this.receivingLambda = lambda;
    }

    connectToDlq(dlq: Sqs) {
        this.dlq = dlq;
    }

    private invokeLambda(): Promise<any> {
        if (!this.receivingLambda || this.messages.length === 0) {
            return Promise.resolve();
        }

        const messagesToProcess = this.messages.filter((message) => message.visible && !message.processing);

        const promises: Array<Promise<void>> = [];
        for (let i = 0; i < messagesToProcess.length; i += this.batchSize) {
            const batch = messagesToProcess.slice(i, i + this.batchSize);
            let promise: Promise<void>;
            try {
                promise = this.receivingLambda.invokeSync(batch.length);
            } catch (e) {
                // Stop because Lambda has reached its limit
                // Will retry later
                return Promise.all(promises);
            }
            // Mark messages as being processed
            batch.map((message) => message.processing = true);
            // We push here the result of `promise.then()` to make sure we store successful promises
            // (else they leak in console.log())
            promises.push(promise.then(
                () => {
                    this.successes += batch.length;
                    batch.map((message) => this.deleteMessage(message));
                },
                () => {
                    batch.forEach((message) => {
                        if (this.retryCount && (message.retries >= this.retryCount)) {
                            this.failedMessage(message);
                        } else {
                            this.retry(message);
                        }
                    });
                },
            ));
        }
        return Promise.all(promises);
    }

    reset() {
        this.messages = [];
        this.successes = 0;
        this.errors = 0;
    }

    outputs(): Array<AwsService> {
        const outputs = [];
        if (this.receivingLambda) {
            outputs.push(this.receivingLambda);
        }
        if (this.dlq) {
            outputs.push(this.dlq);
        }
        return outputs;
    }

    /**
     * Stops the automatic interval of the queue.
     * Used in tests.
     */
    mockInterval(): void {
        window.clearInterval(this.interval);
        this.interval = undefined;
    }
    triggerMockedInterval(): Promise<void> {
        return this.invokeLambda();
    }
}
